import { isFedRamp } from '@atlaskit/atlassian-context/is-fedramp';
import { storage } from '@atlassian/ptc-common';

import siteClient from '../site-client';

const STORAGE_KEY_PREFIX = 'atlassian.people.tenant.';
export const STORAGE_KEY_CLOUD_ID: string = STORAGE_KEY_PREFIX + 'cloudId';

const getTenantInfoFromAvailableProducts = (): Promise<string | undefined> =>
	siteClient.getDefaultCloudIDFromAvailableProducts();

const getTenantInfoFromEdge = async (): Promise<string | undefined> => {
	const resp = await siteClient.getEdgeTenantInfo();
	if (resp && resp.cloudId) {
		return resp.cloudId;
	}
	return undefined;
};

export function putTenantInfoInStorage(cloudId: string | undefined): void {
	if (cloudId) {
		storage.setItem(STORAGE_KEY_CLOUD_ID, cloudId);
	}
}

function getTenantInfoFromStorage(): string | undefined {
	if (!isFedRamp()) {
		// In commercial, we can load the cloudId from edge, we should always do that, not from storage.
		return undefined;
	}

	const cloudId = storage.getItem(STORAGE_KEY_CLOUD_ID);

	// Legacy support for undefined value in storage
	if (!cloudId || cloudId === 'undefined') {
		return;
	}

	return cloudId;
}

let pendingPromise: Promise<string | undefined> | null = null;

const resetPendingPromise = () => {
	pendingPromise = null;
};

/**
 * This function is used only by People Directory standalone app
 */
export function getTenantInfo(): Promise<string | undefined> {
	// Read CloudId from URL params if it exists, to override the env for Standalone app
	const urlParams = new URLSearchParams(window.location.search);
	const cloudId = urlParams.get('cloudId');
	if (cloudId) {
		putTenantInfoInStorage(cloudId);
		return Promise.resolve(cloudId);
	}
	const tenantInfo = getTenantInfoFromStorage();

	if (tenantInfo) {
		return Promise.resolve(tenantInfo);
	}

	// If there is already a request in progress return that
	if (pendingPromise) {
		return pendingPromise;
	}

	if (isFedRamp()) {
		// In FedRamp we use available products to get the cloudId as the URL isn't tenanted.
		// Note - we expect the cloudId query param to be set in the URL for FedRamp.
		pendingPromise = getTenantInfoFromAvailableProducts();
	} else {
		// In commercial, its still e.g https://hello.atlassian.net/people so we use edge to guarantee we get the right cloudId
		pendingPromise = getTenantInfoFromEdge();
	}
	pendingPromise.then(putTenantInfoInStorage).then(resetPendingPromise).catch(resetPendingPromise);

	return pendingPromise;
}

export function getCloudIdAsync(): Promise<string | undefined> {
	return getTenantInfo();
}
